import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	FormHelperText,
	TextField,
	Autocomplete,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Checkbox,
	FormControlLabel,
	Typography,
	CircularProgress,
} from "@mui/material";
// Using Box flex layout instead of Grid
import { useNavigate } from "react-router-dom";
import PageTitle from "../../components/PageTitle/PageTitle";
import "./Register.css";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import AuthedUser from "../AuthedUser/AuthedUser";
import { useAuth } from "../../context/authContext";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ROUTES } from "../../constants";
import { useTranslation } from "react-i18next";
import { registerUser } from "../../services/registerService";
import { sendOTP, verifyEmail } from "../../services/verifyEmailService";
import { AuthPageStyles } from "../../utilities/themeColors";
import CompanyLogo from "../../components/CompanyLogo/CompanyLogo";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import PhoneInput from "../../components/PhoneInput/PhoneInput";
import CountryAutocomplete from "../../components/CountryAutocomplete/CountryAutocomplete";
import {
	getNationalDigits,
	getPhoneMetaForCountry,
	normalizePhoneForCountry,
	resolvePhoneState,
} from "../../utilities/phoneUtils";
import { COUNTRIES_BY_REGION, REGION_OPTIONS } from "../../data/countryRegions";

const Register = () => {
	const initialPhoneState = resolvePhoneState({
		phoneValue: "",
		countryLabel: "United States",
		phoneCountryIso2: "US",
		phoneCountryCode: "1",
	});
	const [formData, setFormData] = useState({
		user_email: "",
		user_password: "",
		confirm_password: "",
		user_first_name: "",
		user_last_name: "",
		user_display_name: "",
		user_phone: initialPhoneState.phoneValue,
		phone_country_code: initialPhoneState.meta.phone_country_code,
		phone_country_iso2: initialPhoneState.meta.phone_country_iso2,
		phone_country_flag: initialPhoneState.meta.phone_country_flag,
		user_role: "Viewer",
		user_status: 0,
		company_name: "",
		street_address: "",
		region: "",
		country: "",
		city: "",
		state_province: "",
		zip_postal_code: "",
		terms_agreed: false, // For storing checkbox state
		purchaser_authority: false, // For storing second checkbox state
	});
	const [error, setError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [emailError, setEmailError] = useState("");
	const [termsError, setTermsError] = useState(""); // For terms validation
	const [validation, setValidation] = useState({
		minLength: false,
		uppercase: false,
		specialChar: false,
		number: false,
		confirmPass: false,
	});
	const navigate = useNavigate();
	const { isAuthenticated } = useAuth();
	const { t } = useTranslation();
	const { authBackdropStyle } = useBrandingAssets();
	const [submitting, setSubmitting] = useState(false);
	const [registrationStep, setRegistrationStep] = useState("form");
	const [pendingUserId, setPendingUserId] = useState(null);
	const [registrationFlowToken, setRegistrationFlowToken] = useState("");
	const [otp, setOtp] = useState("");
	const [otpError, setOtpError] = useState("");
	const [otpInfo, setOtpInfo] = useState("");
	const [verifyingOtp, setVerifyingOtp] = useState(false);
	const [resendingOtp, setResendingOtp] = useState(false);
	const [otpAttemptsRemaining, setOtpAttemptsRemaining] = useState(3);
	const otpSectionRef = useRef(null);
	const formLocked = submitting || registrationStep === "otp";

	// Derived boolean — true when user is blocked (0 attempts left)
	const isOtpBlocked = otpAttemptsRemaining === 0;

	useEffect(() => {
		if (registrationStep !== "otp") return;
		otpSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [registrationStep]);

	const roleOptions = useMemo(() => ([
		{ key: "viewer", value: "Viewer", text: t('MENU_ITEMS.VIEWER') },
		{ key: "purchaser", value: "Purchaser", text: t('MENU_ITEMS.PURCHASER') },
	]), [t]);
	const purchaserConfirmationLabel = useMemo(() => {
		const raw = t('LABELS.PURCHASER_CONFIRMATION');
		return String(raw || "")
			.replace(/__keep_purchase_quote__/g, "purchase request")
			.replace(/\s{2,}/g, " ")
			.trim();
	}, [t]);

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		if (type === "checkbox") {
			setFormData({ ...formData, [name]: checked });
		} else {
			setFormData({ ...formData, [name]: value });
		}

		if (name === "user_email") {
			setEmailError("");
		}
		if (name === "user_password" || name === "confirm_password") {
			setPasswordError("");
		}
	};

	const validatePassword = (password) => {
		const minLength = password.length >= 8;
		const uppercase = /[A-Z]/.test(password);
		const specialChar = /[!@#$%^&*]/.test(password);
		const number = /[0-9]/.test(password);

		setValidation({
			minLength,
			uppercase,
			specialChar,
			number,
		});
	};

	const validateConfirmPassword = (confirmPasswd) => {
		const confirmpass = formData.user_password === confirmPasswd;
		setValidation({
			...validation,
			confirmpass,
		});
	};

	const handlePasswordChange = (e) => {
		const newPassword = e.target.value;
		setFormData({
			...formData,
			user_password: newPassword,
		});
		validatePassword(newPassword);
	};

	const handleConfirmPasswordChange = (e) => {
		setFormData({
			...formData,
			confirm_password: e.target.value,
		});
		validateConfirmPassword(e.target.value);
	};

	const isFormValid = () => {
		const pwdOk = validation.minLength && validation.uppercase && validation.specialChar && validation.number && formData.user_password === formData.confirm_password;
		const termsAccepted = formData.terms_agreed || false;
		const purchaserOk = formData.user_role !== 'Purchaser' || ((formData.terms_agreed || false) && (formData.purchaser_authority || false) && (formData.purchaser_terms_1 || false) && (formData.purchaser_terms_2 || false) && (formData.purchaser_terms_3 || false));
		return pwdOk && termsAccepted && purchaserOk;
	};

	const isValidEmail = (email) => {
		// Allow + in local part
		const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		return emailPattern.test(email);
	};

	const handleRegionChange = (_, nextRegion) => {
		setFormData((prev) => ({
			...prev,
			region: nextRegion || "",
			country: "",
		}));
	};

	const resetOtpFlowState = () => {
		setRegistrationStep("form");
		setPendingUserId(null);
		setRegistrationFlowToken("");
		setOtp("");
		setOtpError("");
		setOtpInfo("");
		setOtpAttemptsRemaining(3);
	};

	// handleRequest NEVER throws — verifyEmail always resolves.
	// All attempts_remaining logic must live in the resolved-value path.
	const handleVerifyOtpForRegistration = async () => {
		if (!registrationFlowToken) {
			setOtpError("Registration session expired. Please register again.");
			resetOtpFlowState();
			return;
		}
		if (isOtpBlocked) {
			setOtpError("You are temporarily blocked. Please request a new OTP.");
			return;
		}
		if (!`${otp || ""}`.trim()) {
			setOtpError("Please enter OTP.");
			return;
		}
		setVerifyingOtp(true);
		setOtpError("");
		setOtpInfo("");
		try {
			const response = await verifyEmail(otp, { flowToken: registrationFlowToken });
			if (response?.status) {
				Swal.fire({
					title: "Email verified successfully",
					text: "Your registration is now submitted for approval. You can log in once your account is approved.",
					icon: "success",
					showCancelButton: false,
				});
				navigate(ROUTES.LOGIN);
				return;
			}

			// KEY FIX: Read attempts_remaining from the RESOLVED response.
			// handleRequest catches all axios errors and returns error.response.data,
			// so this is the only code path that ever executes on failure.
			const rawAttempts =
				response?.error?.attempts_remaining ??
				response?.data?.attempts_remaining ??   // nested under data
				response?.attempts_remaining ??         // or top-level
				response?.data?.data?.attempts_remaining; // or deeper nested
			const attemptsRemaining = Number.isFinite(Number(rawAttempts))
				? Number(rawAttempts)
				: null;
			const registrationDeleted = Boolean(
				response?.error?.registration_deleted ??
				response?.data?.registration_deleted ??
				response?.registration_deleted
			);

			if (attemptsRemaining !== null) {
				console.log("Setting otpAttemptsRemaining to:", attemptsRemaining);
				setOtpAttemptsRemaining(attemptsRemaining);
			}

			if (attemptsRemaining === 0) {
				setOtpError(
					"You have been temporarily blocked due to too many incorrect OTP attempts. Please request a new OTP to continue."
				);
			} else {
				setOtpError(response?.message || "Failed to verify OTP");
			}

		} catch (err) {
			// Safety net only — handleRequest won't reach here under normal conditions.
			console.error("Unexpected OTP verification error:", err);
			setOtpError("An unexpected error occurred. Please try again.");
		} finally {
			setVerifyingOtp(false);
		}
	};

	// Same fix for resend — handleRequest never throws, check resolved response.
	const handleResendOtp = async () => {
		if (!registrationFlowToken) {
			setOtpError("Registration session expired. Please register again.");
			resetOtpFlowState();
			return;
		}
		const wasBlocked = isOtpBlocked;
		setResendingOtp(true);
		setOtpError("");
		setOtpInfo("");
		try {
			const response = await sendOTP({ flowToken: registrationFlowToken }, { forceResend: true });

			// handleRequest returns error.response.data on failure, never throws
			const succeeded = response?.status === true || response?.status === 1;
			if (!succeeded) {
				const message = response?.message || response?.error || "Failed to resend OTP";
				setOtpError(message);
				return;
			}

			setOtp("");
			setOtpAttemptsRemaining(3);
			setOtpInfo("A new OTP has been sent to your email.");
		} catch (err) {
			setOtpError("An unexpected error occurred. Please try again.");
		} finally {
			setResendingOtp(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (registrationStep === "otp") {
			await handleVerifyOtpForRegistration();
			return;
		}
		if (submitting) return;
		setError("");
		setTermsError("");

		const req_body = {
			...formData,
			user_display_name: `${formData.user_first_name} ${formData.user_last_name}`,
			terms_agreed: Boolean(formData.terms_agreed),
			purchaser_authority: Boolean(formData.purchaser_authority),
			purchaser_terms_1: Boolean(formData.purchaser_terms_1),
			purchaser_terms_2: Boolean(formData.purchaser_terms_2),
			purchaser_terms_3: Boolean(formData.purchaser_terms_3),
		};

		// Password and email validation
		if (formData.user_password !== formData.confirm_password) {
			setPasswordError(t('ALERT_MESSAGES.PASSWORDS_DO_NOT_MATCH'));
			return;
		}

		if (!isValidEmail(formData.user_email)) {
			setEmailError(t('ALERT_MESSAGES.PLEASE_ENTER_VALID_EMAIL_ADDRESS'));
			return;
		}
		// Require region and country
		if (!String(formData.region || "").trim() || !String(formData.country || "").trim()) {
			setError(t('ALERT_MESSAGES.SELECT_REGION_AND_COUNTRY'));
			return;
		}
		const nationalPhoneDigits = getNationalDigits(formData.user_phone || "", formData.phone_country_iso2 || "US");
		if (!nationalPhoneDigits) {
			setError(t("GENERAL.PHONE_NUMBER_IS_REQUIRED"));
			return;
		}

		// Terms and conditions validation (required for all roles)
		if (!formData.terms_agreed) {
			setTermsError(t('ALERT_MESSAGES.YOU_MUST_AGREE_BOTH_TERMS') || "Please accept terms and conditions to proceed.");
			return;
		}

		// Additional purchaser terms validation
		if (formData.user_role === "Purchaser") {
			if (!(formData.purchaser_authority && formData.purchaser_terms_1 && formData.purchaser_terms_2 && formData.purchaser_terms_3)) {
				setTermsError(t('ALERT_MESSAGES.AGREE_TO_ALL_PURCHASER_TERMS'));
				return;
			}
		}

		setSubmitting(true);
		try {
			const response = await registerUser(req_body);
			if (response.status) {
				const serverUserId = response?.data?.user_id;
				const serverFlowToken = response?.data?.flow_token;
				const attemptsRemaining = Number(response?.data?.attempts_remaining);
				if (serverUserId) {
					setPendingUserId(serverUserId);
				}
				if (serverFlowToken) {
					setRegistrationFlowToken(serverFlowToken);
				}
				if (Number.isFinite(attemptsRemaining)) {
					setOtpAttemptsRemaining(attemptsRemaining);
				}
				setRegistrationStep("otp");
				setOtp("");
				setOtpError("");
				setOtpInfo("We sent an OTP to your email. Verify it to complete registration.");
			} else {
				const message = response?.message || response?.error_message || t('ALERT_MESSAGES.ERROR_OCCURED');
				if (message.toLowerCase().includes("email already exists") || message.toLowerCase().includes("email already registered")) {
					Swal.fire({
						title: "Email Already Registered",
						text: "An account with this email already exists. Please use a different email or log in.",
						icon: "warning",
						confirmButtonText: "OK",
					});
					setEmailError(message);
				} else {
					setError(message);
					Swal.fire({ title: "Registration Error", text: message, icon: "error", confirmButtonText: "OK" });
				}
			}
		} catch (error) {
			console.error("Unexpected registration error:", error);
			const message = t('ALERT_MESSAGES.ERROR_OCCURED');
			setError(message);
			Swal.fire({ title: "Error", text: message, icon: "error", confirmButtonText: "OK" });
		} finally {
			setSubmitting(false);
		}
	};

	if (isAuthenticated) {
		return <AuthedUser />;
	}

	return (
		<Box className="register-container" sx={authBackdropStyle || AuthPageStyles}>
			<Box className="register-form-container">
				<CompanyLogo alt="Logo" className="register-logo" />

				{/* Make form a flex column that fills height */}
				<form
					onSubmit={handleSubmit}
					style={{
						display: 'flex',
						flexDirection: 'column',
						flexGrow: 1,
						width: '100%',
						height: '100%', // important
						overflow: 'hidden',
					}}
				>
					<PageTitle title={t('TITLES.REGISTER')} style />
					{/* Scrollable content area */}
					<Box
						sx={{
							flexGrow: 1,
							overflowY: 'auto',
							display: 'flex',
							flexDirection: 'column',
							rowGap: 3,
							width: '100%',
							px: 1,
							scrollbarWidth: 'thin',

							/* optional for WebKit browsers */
							'&::-webkit-scrollbar': {
								width: '6px',
							},
							'&::-webkit-scrollbar-thumb': {
								backgroundColor: '#bbb',
								borderRadius: '3px',
							},
						}}
					>
						<fieldset
							disabled={formLocked}
							style={{ border: 0, margin: 0, padding: 0, minInlineSize: "auto" }}
						>

							<Typography variant="body1" fontWeight={"bold"} mb={1.5}>{t('DIVIDER.USER_DETAILS')}</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'nowrap', flexDirection: 'column', gap: 2, width: "100%" }}>
								<Box sx={{ display: 'flex', flexWrap: 'nowrap', columnGap: 2, width: '100%' }}>
									<TextField
										label={t('LABELS.FIRST_NAME')}
										variant="outlined"
										fullWidth
										required
										name="user_first_name"
										value={formData.user_first_name}
										onChange={handleChange}
										size="small"

									/>
									<TextField
										label={t('LABELS.LAST_NAME')}
										variant="outlined"
										fullWidth
										required
										name="user_last_name"
										value={formData.user_last_name}
										onChange={handleChange}
										size="small"
									/>

								</Box>
								<Box sx={{ display: 'flex', flexWrap: 'nowrap', columnGap: 2, width: '100%' }}>
									<Box sx={{ width: { xs: '100%', md: '100%' } }}>
										<TextField
											label={t('TABLE_HEADERS.EMAIL')}
											variant="outlined"
											fullWidth
											required
											name="user_email"
											type="email"
											value={formData.user_email}
											onChange={handleChange}
											size="small"
										/>
										{emailError && <FormHelperText error>{emailError}</FormHelperText>}
									</Box>
									<PhoneInput
										value={formData.user_phone || ""}
										countryIso2={formData.phone_country_iso2}
										countryCode={formData.phone_country_code}
										countryLabel={formData.country}
										disabled={formLocked}
										onChange={(value) => setFormData((prev) => ({ ...prev, user_phone: value }))}
										onCountryChange={(country) => {
											const phoneMeta = getPhoneMetaForCountry(country);
											setFormData((prev) => ({
												...prev,
												user_phone: normalizePhoneForCountry(prev.user_phone || "", country),
												...phoneMeta,
											}));
										}}
										label={t('LABELS.PHONE_NUMBER')}
										required
										size="small"
									/>

								</Box>
								<TextField
									label={t('LABELS.PASSWORD')}
									variant="outlined"
									fullWidth
									required
									name="user_password"
									type="password"
									value={formData.user_password}
									onChange={handlePasswordChange}
									size="small"
								/>
								<TextField
									label={t('LABELS.CONFIRM_PASSWORD')}
									variant="outlined"
									fullWidth
									required
									name="confirm_password"
									type="password"
									value={formData.confirm_password}
									onChange={handleConfirmPasswordChange}
									size="small"
								/>

								{passwordError && <FormHelperText error>{passwordError}</FormHelperText>}

								<div className="password-validation">
									<p
										style={{
											fontSize: "11px",
											color: validation.minLength ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.minLength ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										{t('UI_TEXT.MINIMUM_EIGHT_CHARACTERS')}
									</p>
									<p
										style={{
											fontSize: "11px",
											color: validation.uppercase ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.uppercase ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										{t('UI_TEXT.AT_LEAST_ONE_UPPERCASE')}
									</p>
									<p
										style={{
											fontSize: "11px",
											color: validation.specialChar ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.specialChar ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										{t('UI_TEXT.AT_LEAST_ONE_SPECIAL_CHARACTERS')}
									</p>
									<p
										style={{
											fontSize: "11px",
											color: validation.number ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.number ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										{t('UI_TEXT.AT_LEAST_ONE_NUMBER')}
									</p>
									<p
										style={{
											fontSize: "11px",
											color: validation.confirmpass ? "green" : "#aaa",
											marginBottom: 0,
										}}>
										{validation.confirmpass ? (
											<CheckCircleIcon fontSize="11px" />
										) : (
											<ErrorOutlineIcon fontSize="11px" />
										)}{" "}
										{t('UI_TEXT.CONFIRM_PASSWORD_MATCHES')}
									</p>
								</div>
							</Box>

							<Typography variant="body1" fontWeight={"bold"} mt={2} mb={1}>{t('TITLES.COMPANY_DETAILS')}</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'nowrap', flexDirection: 'column', gap: 2, width: "100%" }}>
								<Box sx={{ display: 'flex', flexWrap: 'nowrap', columnGap: 2, width: '100%' }}>
									<TextField
										label={t('LABELS.COMPANY_NAME')}
										variant="outlined"
										fullWidth
										required
										name="company_name"
										value={formData.company_name}
										onChange={handleChange}
										size="small"
									/>
									<TextField
										label={t('LABELS.STREET_ADDRESS')}
										variant="outlined"
										fullWidth
										required
										name="street_address"
										value={formData.street_address}
										onChange={handleChange}
										size="small"
									/>
								</Box>
								<Box sx={{ display: 'flex', flexWrap: 'nowrap', columnGap: 2, width: '100%' }}>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Autocomplete
											options={REGION_OPTIONS}
											value={formData.region || null}
											disabled={formLocked}
											onChange={handleRegionChange}
											autoHighlight
											selectOnFocus
											handleHomeEndKeys
											openOnFocus
											renderInput={(params) => (
												<TextField
													{...params}
													label={t('LABELS.REGION') || 'Region'}
													required
													size="small"
												/>
											)}
											fullWidth
										/>
									</Box>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<CountryAutocomplete
											key={formData.region || "no-region"}
											options={COUNTRIES_BY_REGION[formData.region] || []}
											value={formData.country}
											onChange={(nextCountryLabel) => {
												setFormData((prev) => {
													const nextPhoneMeta = getPhoneMetaForCountry(nextCountryLabel);
													return {
														...prev,
														country: nextCountryLabel,
														user_phone: normalizePhoneForCountry(prev.user_phone || "", nextCountryLabel),
														...nextPhoneMeta,
													};
												});
											}}
											label={t('LABELS.COUNTRY') || 'Country'}
											required
											disabled={!formData.region || formLocked}
											margin="none"
										/>
									</Box>
								</Box>
								<Box sx={{ display: 'flex', flexWrap: 'nowrap', columnGap: 2, width: '100%' }}>
									<TextField
										label={t('LABELS.CITY') || 'City'}
										variant="outlined"
										fullWidth
										name="city"
										value={formData.city}
										onChange={handleChange}
										size="small"
									/>
									<TextField
										label={t('LABELS.STATE_PROVINCE') || 'State/Province'}
										variant="outlined"
										fullWidth
										name="state_province"
										value={formData.state_province}
										onChange={handleChange}
										size="small"
									/>
									<TextField
										label={t('LABELS.ZIP_POSTAL_CODE') || 'Zip/Postal Code'}
										variant="outlined"
										fullWidth
										name="zip_postal_code"
										value={formData.zip_postal_code}
										onChange={handleChange}
										size="small"
									/>
								</Box>
							</Box>
							<Typography variant="body1" fontWeight={"bold"} mt={2} mb={1.5}>{t('TITLES.USER_ROLE')}</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'nowrap', flexDirection: 'column', gap: 2, width: "100%" }}>
								<FormControl fullWidth size="small" sx={{ width: "100%" }}>
									<InputLabel>{t('LABELS.SELECT_ROLE')}</InputLabel>
									<Select
										label={t('LABELS.SELECT_ROLE')}
										name="user_role"
										value={formData.user_role}
										onChange={handleChange}
										sx={{ width: "100%" }}>
										{roleOptions.map((role) => (
											<MenuItem key={role.key} value={role.value}>
												{role.text}
											</MenuItem>
										))}
									</Select>
									<FormHelperText>This is your intended role. Admins may adjust during approval.</FormHelperText>
								</FormControl>
							</Box>

							<Typography variant="body1" mt={2} fontWeight={"bold"}>{t('TITLES.TERMS_AND_CONDITIONS')}</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'nowrap', flexDirection: 'column', gap: 0, width: "100%" }}>
								<FormControlLabel
									sx={{ '& .MuiFormControlLabel-label': { fontSize: '11px' } }}
									control={
										<Checkbox
											checked={formData.terms_agreed || false}
											onChange={handleChange}
											name="terms_agreed"
										/>
									}
									label={t('LABELS.ROLE_CONFIRMATION')}
								/>

								{formData.user_role === 'Purchaser' && (
									<>
										<FormControlLabel
											sx={{ '& .MuiFormControlLabel-label': { fontSize: '11px' } }}
											control={
												<Checkbox
													checked={formData.purchaser_authority || false}
													onChange={handleChange}
													name="purchaser_authority"
												/>
											}
											label={purchaserConfirmationLabel}
										/>
										<FormControlLabel
											sx={{ '& .MuiFormControlLabel-label': { fontSize: '11px' } }}
											control={
												<Checkbox
													checked={formData.purchaser_terms_1 || false}
													onChange={handleChange}
													name="purchaser_terms_1"
												/>
											}
											label={t('COMPANY.I_CONFIRM_I_AM_AUTHORIZED_TO_PLACE_PURCHASE_REQUESTS')}
										/>
										<FormControlLabel
											sx={{ '& .MuiFormControlLabel-label': { fontSize: '11px' } }}
											control={
												<Checkbox
													checked={formData.purchaser_terms_2 || false}
													onChange={handleChange}
													name="purchaser_terms_2"
												/>
											}
											label={t('COMPANY.I_AGREE_TO_FOLLOW_COMPANY_PROCUREMENT_POLICIES')}
										/>
										<FormControlLabel
											sx={{ '& .MuiFormControlLabel-label': { fontSize: '11px' } }}
											control={
												<Checkbox
													checked={formData.purchaser_terms_3 || false}
													onChange={handleChange}
													name="purchaser_terms_3"
												/>
											}
											label={t('EULA_ADMIN.I_ACKNOWLEDGE_THAT_ORDERS_MAY_REQUIRE_ADMIN_APPROVAL')}
										/>
									</>
								)}

								{termsError && <FormHelperText error>{termsError}</FormHelperText>}
							</Box>
						</fieldset>
						<Box sx={{ display: 'flex', flexWrap: 'nowrap', flexDirection: 'column', gap: 2, width: "100%" }}>
							{error && <p className="error-text">{error}</p>}
							{registrationStep === "otp" && (
								<Box ref={otpSectionRef} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
									<Typography variant="body1" fontWeight={"bold"} mt={2} mb={1}>
										{t('TITLES.VERIFY_EMAIL')}
									</Typography>
									<Typography variant="body2" fontSize={"11px"} color="text.secondary">
										Verify the OTP sent to <b>{formData.user_email}</b> to complete registration.
									</Typography>
									<TextField
										label={t('PLACEHOLDER.ENTER_OTP') || "Enter OTP"}
										variant="outlined"
										fullWidth
										name="register_otp"
										value={otp}
										disabled={isOtpBlocked}
										onChange={(e) => {
											setOtp(e.target.value);
											setOtpError("");
										}}
										size="small"
									/>
									{otpError && <FormHelperText error>{otpError}</FormHelperText>}
									{otpInfo && <FormHelperText sx={{ color: "success.main" }}>{otpInfo}</FormHelperText>}
									{otpError && otpAttemptsRemaining < 3 && otpAttemptsRemaining >= 0 && (
										<FormHelperText>
											Attempts remaining: {otpAttemptsRemaining}
										</FormHelperText>
									)}
									<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
										<Button
											variant="contained"
											color="primary"
											type="button"
											onClick={handleVerifyOtpForRegistration}
											disabled={verifyingOtp || isOtpBlocked}
											sx={{ minWidth: "120px" }}
										>
											{verifyingOtp ? "Verifying..." : "Verify OTP"}
										</Button>
										<Button
											variant="outlined"
											type="button"
											onClick={handleResendOtp}
											disabled={resendingOtp || isOtpBlocked}
										>
											{resendingOtp ? "Sending..." : "Send Again"}
										</Button>
										<Button
											variant="text"
											type="button"
											onClick={resetOtpFlowState}
										>
											Start Over
										</Button>
									</Box>
								</Box>
							)}
							{registrationStep !== "otp" && (
								<Button
									disabled={!isFormValid() || submitting}
									variant="contained"
									color="primary"
									type="submit"
									sx={{ minWidth: "120px" }}>
									{submitting ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
											Registering...
										</>
									) : (
										t('BUTTON.REGISTER')
									)}
								</Button>
							)}
							<div className="login-link-container">
								{t('UI_TEXT.LOGIN_PROMPT_PREFIX')} <Link to={ROUTES.LOGIN}>{t('UI_TEXT.LOGIN_LINK_TEXT')}</Link>
							</div>

						</Box>

					</Box>

				</form>

			</Box>
		</Box>
	);
};

export default Register;
